import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  Link,
  Form,
  FormLayout,
  TextField,
  TextArea,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { hydraUiApi } from '@/services/api';
import { generate } from '@customerio/hydra-copilot';
import { z } from '@customerio/hydra-validation';

import { useZodForm, useWatch } from '@/components/form';

import { sentryCaptureException } from '@/utils/sentry';
import { supportFormSchema, GOAL_PLACEHOLDER, isDefaultGoalText, type SupportFormData } from '../helpers/form-schema';
import { composeGoalWithDiagnostics } from '../helpers/diagnostics';
import { DiagnosticsDisclosure } from './diagnostics-disclosure';
import { SupportAccessConsent } from './support-access-consent';
import { DEFAULT_SUPPORT_TEAM_ACCESS_PERIOD_IN_DAYS } from '../types';
import type { SupportFormModalData } from '../types';
import { useEffect, useRef } from 'react';
import { useAnalytics } from '@/services/analytics';
import { Events } from '@/services/analytics/types';

export function SupportFormModal({
  data,
  onConfirm,
  onClose,
  modalState,
}: PlumaManagedModalProps<SupportFormModalData>) {
  const snackbars = useSnackbars();
  const { track } = useAnalytics();

  const hasTrackedStarted = useRef(false);
  useEffect(() => {
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
    if (data.source === 'cancellation_flow' && !hasTrackedStarted.current && data.workspaceId != null) {
      hasTrackedStarted.current = true;
      void hydraUiApi.tracking.createTrackEventForEnvironment({
        params: { path: { environment_id: String(data.workspaceId) } },
        body: {
          event: Events.plan_and_billing.supportRequestStarted.name,
          properties: {
            accountId: data.accountId,
            workspaceId: data.workspaceId,
            source: data.source,
          },
        },
      });
    }
  }, [data.source, data.accountId, data.workspaceId]);

  const form = useZodForm({
    schema: supportFormSchema,
    defaultValues: {
      goal: data.initialProblem ?? '',
      video: '',
      consentToAccess: false,
      daysLimit: DEFAULT_SUPPORT_TEAM_ACCESS_PERIOD_IN_DAYS,
    },
    mode: 'onChange',
  });

  const consentToAccess = useWatch({ control: form.control, name: 'consentToAccess' });
  const goal = useWatch({ control: form.control, name: 'goal' });
  const daysLimit = useWatch({ control: form.control, name: 'daysLimit' });
  const { isValid, isSubmitting } = form.formState;
  const canSubmit = isValid && !isDefaultGoalText(goal);

  const onSubmit = form.handleSubmit(async (value: SupportFormData) => {
    void track('inline_support', 'submitClicked', data.trackingProperties);

    try {
      if (!data.supportTeamAccessEnabled && value.consentToAccess) {
        const now = Math.floor(Date.now() / 1000);
        const timestamp = value.consentToAccess ? now + value.daysLimit * 24 * 60 * 60 : 0;

        try {
          await hydraUiApi.accounts.setSupportAccess({
            params: { path: { account_id: String(data.accountId) } },
            body: {
              account: {
                access_granted_until: timestamp,
              },
            },
          });
          void track('inline_support', 'supportAccessGranted', data.trackingProperties);
        } catch (error) {
          void track('inline_support', 'supportAccessError', data.trackingProperties);
          throw error;
        }
      }

      let subject = 'No comment provided.';
      try {
        const result = await generate(
          'Generate a short, concise subject line for this support ticket based on the problem description. The subject should help support triage the issue quickly. Maximum 85 characters.',
          z.toJSONSchema(
            z.object({
              subject: z.string().describe('A short subject line for the support ticket').min(1).max(85),
            }),
          ),
          {
            context: [
              {
                description: 'The user problem description',
                value: value.goal,
              },
            ],
            includeGlobalContext: false,
          },
        );
        subject = result.subject;
      } catch (error) {
        sentryCaptureException(error, {
          level: 'warning',
          tags: { feature: 'support-form-ai-subject-generation' },
        });
        subject = 'AI summary failed.';
      }

      await hydraUiApi.support.create({
        params: { path: { account_id: String(data.accountId) } },
        body: {
          comment: subject,
          goal: composeGoalWithDiagnostics(value.goal, data.diagnostics),
          video: value.video,
          replyTo: data.userEmail,
          consent_to_access: value.consentToAccess,
          browser: navigator.userAgent,
          url: window.location.href,
          windowDimensions: `${window.innerWidth}x${window.innerHeight}`,
        },
      });

      snackbars.create({
        message: "Your support request has been submitted. We'll get back to you soon!",
        variant: 'success',
      });

      void track('inline_support', 'submitSuccess', data.trackingProperties);

      if (data.source === 'cancellation_flow' && data.workspaceId != null) {
        void hydraUiApi.tracking.createTrackEventForEnvironment({
          params: { path: { environment_id: String(data.workspaceId) } },
          body: {
            event: Events.plan_and_billing.supportRequestCompleted.name,
            properties: {
              accountId: data.accountId,
              workspaceId: data.workspaceId,
              comment: value.goal,
              source: data.source,
            },
          },
        });
      }

      onConfirm(value);
    } catch (error) {
      void track('inline_support', 'submitError', data.trackingProperties);
      sentryCaptureException(error);
      snackbars.create({
        message: 'Unable to submit support request. Please try again.',
        variant: 'error',
      });
    }
  });

  const handleClose = () => {
    void track('inline_support', 'closed', data.trackingProperties);
    onClose();
  };

  return (
    <Modal state={modalState} size="md" title="Describe your support request">
      <ModalBody>
        <Form id="support-form" onSubmit={onSubmit}>
          <FormLayout>
            <TextArea
              {...form.registerPluma('goal')}
              label="Describe your problem"
              description="The more detail you can share the better we can help."
              placeholder={GOAL_PLACEHOLDER}
              minRows={5}
            />

            {data.diagnostics != null ? <DiagnosticsDisclosure diagnostics={data.diagnostics} /> : null}

            <TextField
              {...form.registerPluma('video')}
              label="Share a video"
              placeholder="Video URL"
              description={
                <>
                  Videos are recommended but not required. eg.{' '}
                  <Link href="http://loom.com/" variant="secondary">
                    Loom
                  </Link>
                </>
              }
            />

            <SupportAccessConsent
              data={data}
              consentToAccess={consentToAccess}
              daysLimit={daysLimit}
              daysLimitError={form.formState.errors.daysLimit?.message}
              onConsentChange={(isChecked) => {
                void form.setValue('consentToAccess', isChecked, { shouldValidate: true });
              }}
              onDaysLimitChange={(days) => {
                void form.setValue('daysLimit', days, { shouldValidate: true, shouldDirty: true });
              }}
            />
          </FormLayout>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="subtle" onClick={handleClose} isDisabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="primary" form="support-form" type="submit" isDisabled={!canSubmit} isLoading={isSubmitting}>
          Submit ticket
        </Button>
      </ModalFooter>
    </Modal>
  );
}
